import React, { useState } from "react";
import classNames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import PersonIcon from "@material-ui/icons/Person";
import { intersection, uniq } from "lodash";
import Grid from "@material-ui/core/Grid";
import DeleteIcon from "@material-ui/icons/Delete";
import DownloadIcon from "@material-ui/icons/GetApp";
import EventIcon from "@material-ui/icons/Event";
import withContext from "../../../tools/withContext";
import BatchDeadlineFormDialog from "./BatchDeadlineFormDialog";
import BatchAttribFormDialog from "./BatchAttribFormDialog";
import BatchesDeleteDialog from "./BatchesDeleteDialog";
import UiButton from "../../common/UiButton";
import BatchType from "../../../types/Batch";
import UserType from "../../../types/User";
import constants from "../../../tools/constants";
import requestFileDownload from "./requestDownloadFile";
import { tableTheme } from "../../common/tableTheme";
import { MAX_BATCHES_EXPORT } from "../constants";

const styles = (theme: Theme) =>
  createStyles({
    smallButton: {
      padding: "9px 16px",
    },
    actionsBarModern: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap" as const,
      gap: "6px",
      padding: "4px 8px 4px 10px",
      border: `1px solid ${tableTheme.border}`,
      borderRadius: tableTheme.radiusSm,
      backgroundColor: tableTheme.headerBg,
    },
    selectedCountModern: {
      fontSize: "0.8125rem",
      color: tableTheme.secondaryDark,
      fontWeight: 500,
      whiteSpace: "nowrap" as const,
      paddingRight: 6,
      marginRight: 2,
    },
    actionsClusterModern: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    actionsDividerModern: {
      width: 1,
      alignSelf: "stretch",
      minHeight: 24,
      backgroundColor: tableTheme.border,
      margin: "2px 2px",
      flexShrink: 0,
    },
    actionIconModern: {
      width: 28,
      height: 28,
      padding: 4,
      border: `1px solid ${tableTheme.border}`,
      borderRadius: tableTheme.radiusSm,
      backgroundColor: tableTheme.surface,
      color: tableTheme.textPrimary,
      "&:hover": {
        backgroundColor: tableTheme.secondarySoft,
        borderColor: tableTheme.secondaryLight,        
      },
    },
    actionIconDangerModern: {
      color: tableTheme.secondaryDark,
      borderColor: "rgba(255, 94, 98, 0.35)",
      "&:hover": {
        backgroundColor: "rgba(255, 94, 98, 0.08)",
        borderColor: tableTheme.secondary,
        color: tableTheme.secondaryDark,
      },
    },
    actionTextModern: {
      minHeight: 28,
      height: 28,
      padding: "2px 8px",
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: "none" as const,
      borderRadius: tableTheme.radiusSm,
      border: `1px solid ${tableTheme.border}`,
      backgroundColor: tableTheme.surface,
      color: tableTheme.textPrimary,
      boxShadow: "none",
      "&:hover": {
        backgroundColor: tableTheme.secondarySoft,
        borderColor: tableTheme.secondaryLight,
      },
    },
    actionTextIconModern: {
      fontSize: 16,
      marginRight: 4,
    },
    actionTextSpinnerModern: {
      marginRight: 4,
    },
    tooltip: {
      fontSize: 14,
    },
    icon: {
      marginRight: theme.spacing.unit,
    },
  });

interface PropsType {
  saveBatch: any;
  deleteBatch: any;
  ctx: any;
  classes: any;
  batches: BatchType[];
  users: UserType[];
  selected: number[];
  selectNone: Function;
  variant?: "default" | "modern";
}

const BatchesActionButton = (props: PropsType) => {
  const {
    users,
    batches,
    classes,
    saveBatch,
    deleteBatch,
    selected,
    selectNone,
    variant = "default",
    ctx: {
      user: {
        authorities: [userRole],
        token,
      },
    },
  } = props;
  const isModern = variant === "modern";
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [view, setView] = useState("attrib");
  const [isDialogDeleteOpen, setIsDialogDeleteOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState<"csv" | "json" | null>(
    null,
  );

  const openDialog = (viewType: "attrib" | "deadline") => {
    setIsDialogOpen(true);
    setView(viewType);
  };

  const closeDialog = () => setIsDialogOpen(false);

  const openDialogDelete = () => setIsDialogDeleteOpen(true);

  const closeDialogDelete = () => setIsDialogDeleteOpen(false);

  const handleSave = (attribution: any) =>
    saveBatch(attribution).then(() => {
      selectNone([]);
      closeDialog();
    });

  const batchesSelected = batches.filter(
    (b: BatchType) => selected.indexOf(b.id) !== -1
  );

  const exportDisabled =
    selected.length === 0 || selected.length > MAX_BATCHES_EXPORT;
  const exportTooltipBase = (format: string) =>
    exportDisabled && selected.length > MAX_BATCHES_EXPORT
      ? `L'export ${format} est limité à ${MAX_BATCHES_EXPORT} lots maximum`
      : `Exporter au format ${format} les lots sélectionnés`;

  const batchesSelectedProjectsIds = uniq(
    batchesSelected.reduce((m: any, b: any) => m.concat(b.project.id), [])
  );

  const usersForSelection = users.filter((u: any) => {
    const i = intersection(u.projects, batchesSelectedProjectsIds);
    return (
      (i.length > 0 && i.length === batchesSelectedProjectsIds.length) ||
      u.authorities.find((a: string) => a === "ROLE_ADMIN")
    );
  });

  const backHost = `${process.env.REACT_APP_API_URL}`;
  const tooltipClasses = { tooltip: classes.tooltip };
  const tooltipPopperProps = {
    modifiers: {
      offset: {
        enabled: true,
        offset: "0, 3",
      },
    },
  };

  const renderDefaultButton = (
    title: string,
    onClick: () => void,
    children: React.ReactNode,
    disabled = false,
  ) => (
    <Tooltip title={title} classes={tooltipClasses} PopperProps={tooltipPopperProps}>
      <span>
        <UiButton
          color="secondary"
          variant="contained"
          className={classes.smallButton}
          onClick={onClick}
          disabled={disabled}
        >
          {children}
        </UiButton>
      </span>
    </Tooltip>
  );

  const renderModernIconButton = (
    title: string,
    onClick: () => void,
    icon: React.ReactNode,
    disabled = false,
    danger = false,
  ) => (
    <Tooltip title={title} classes={tooltipClasses} PopperProps={tooltipPopperProps}>
      <span>
        <IconButton
          className={classNames(classes.actionIconModern, {
            [classes.actionIconDangerModern]: danger,
          })}
          onClick={onClick}
          disabled={disabled}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const renderModernTextButton = (
    title: string,
    onClick: () => void,
    label: string,
    loading = false,
    disabled = false,
  ) => (
    <Tooltip title={title} classes={tooltipClasses} PopperProps={tooltipPopperProps}>
      <span>
        <UiButton
          variant="outlined"
          className={classes.actionTextModern}
          onClick={onClick}
          disabled={disabled || loading}
        >
          {loading ? (
            <CircularProgress
              size={14}
              color="inherit"
              className={classes.actionTextSpinnerModern}
            />
          ) : (
            <DownloadIcon className={classes.actionTextIconModern} />
          )}
          {label}
        </UiButton>
      </span>
    </Tooltip>
  );

  const exportCsv = async () => {
    if (exportLoading || exportDisabled) return;
    setExportLoading("csv");
    try {
      await requestFileDownload(
        "GET",
        `${backHost}${constants.paths.BATCHES_EXPORT_PATH}?type=CSV&batches=${selected.join(",")}`,
        token,
        "",
        "application/json",
      );
    } finally {
      setExportLoading(null);
    }
  };

  const exportJson = async () => {
    if (exportLoading || exportDisabled) return;
    setExportLoading("json");
    try {
      await requestFileDownload(
        "GET",
        `${backHost}${constants.paths.BATCHES_EXPORT_PATH}?type=JSON&batches=${selected.join(",")}`,
        token,
        "",
        "application/json",
      );
    } finally {
      setExportLoading(null);
    }
  };

  const dialogs = (
    <>
      {selected.length > 0 && view === "deadline" && (
        <BatchDeadlineFormDialog
          open={isDialogOpen}
          onClose={closeDialog}
          onSave={handleSave}
          users={usersForSelection}
          selected={selected}
        />
      )}
      {selected.length > 0 && view === "attrib" && (
        <BatchAttribFormDialog
          open={isDialogOpen}
          onClose={closeDialog}
          onSave={handleSave}
          users={usersForSelection}
          batches={batchesSelected}
          selected={selected}
        />
      )}
      {selected.length > 1 && (
        <BatchesDeleteDialog
          open={isDialogDeleteOpen}
          onClose={closeDialogDelete}
          deleteBatch={deleteBatch}
          users={users}
          batches={batchesSelected}
          selected={selected}
        />
      )}
    </>
  );

  if (isModern && selected.length > 1) {
    const selectionLabel = `${selected.length} sélectionné${selected.length > 1 ? "s" : ""}${
      userRole === "ROLE_ADMIN" && selected.length > MAX_BATCHES_EXPORT
        ? ` (export max. ${MAX_BATCHES_EXPORT})`
        : ""
    }`;

    return (
      <>
        {dialogs}
        <div className={classes.actionsBarModern}>
          <span className={classes.selectedCountModern}>{selectionLabel}</span>
          <div className={classes.actionsDividerModern} />
          <div className={classes.actionsClusterModern}>
            {renderModernIconButton(
              "Modifier l'attribution des lots sélectionnés",
              () => openDialog("attrib"),
              <PersonIcon fontSize="small" />,
              usersForSelection.length === 0,
            )}
            {renderModernIconButton(
              "Modifier la date limite des lots sélectionnés",
              () => openDialog("deadline"),
              <EventIcon fontSize="small" />,
              usersForSelection.length === 0,
            )}
          </div>
          {userRole === "ROLE_ADMIN" && (
            <>
              <div className={classes.actionsDividerModern} />
              <div className={classes.actionsClusterModern}>
                {renderModernTextButton(
                  exportTooltipBase("CSV"),
                  exportCsv,
                  "CSV",
                  exportLoading === "csv",
                  exportDisabled || (exportLoading !== null && exportLoading !== "csv"),
                )}
                {renderModernTextButton(
                  exportTooltipBase("JSON"),
                  exportJson,
                  "JSON",
                  exportLoading === "json",
                  exportDisabled || (exportLoading !== null && exportLoading !== "json"),
                )}
              </div>
            </>
          )}
          <div className={classes.actionsDividerModern} />
          {renderModernIconButton(
            "Supprimer les lots sélectionnés",
            openDialogDelete,
            <DeleteIcon fontSize="small" />,
            false,
            true,
          )}
        </div>
      </>
    );
  }

  if (isModern) {
    return <>{dialogs}</>;
  }

  return (
    <Grid container spacing={8} direction="row">
      {dialogs}
      {selected.length > 1 && (
        <Grid item>
          {renderDefaultButton(
            "Modifier l'attribution des lots sélectionnés",
            () => openDialog("attrib"),
            <PersonIcon />,
            usersForSelection.length === 0,
          )}
        </Grid>
      )}
      {selected.length > 1 && (
        <Grid item>
          {renderDefaultButton(
            "Modifier la date limite des lots sélectionnés",
            () => openDialog("deadline"),
            <EventIcon />,
            usersForSelection.length === 0,
          )}
        </Grid>
      )}
      {selected.length > 1 &&
        userRole === "ROLE_ADMIN" && [
          <Grid item key="csv">
            {renderDefaultButton(
              exportTooltipBase("CSV"),
              exportCsv,
              <>
                <DownloadIcon className={classes.icon} />
                CSV
              </>,
              exportDisabled,
            )}
          </Grid>,
          <Grid item key="json">
            {renderDefaultButton(
              exportTooltipBase("JSON"),
              exportJson,
              <>
                <DownloadIcon className={classes.icon} />
                JSON
              </>,
              exportDisabled,
            )}
          </Grid>,
        ]}
      {selected.length > 1 && (
        <Grid item>
          {renderDefaultButton(
            "Supprimer les lots sélectionnés",
            openDialogDelete,
            <DeleteIcon />,
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default withStyles(styles)(withContext(BatchesActionButton));
